import { Button } from '@heroui/react'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FormModal } from '@/components/ui/form-modal'
import { TextFieldControl } from '@/components/ui/form-fields'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import { toTranslation } from '@/lib/api/common'

import { useOptionSetActions } from '../hooks'
import type { OptionSet, SaveOptionSetBody } from '../types'

const schema = z.object({
  nameEn: z.string().trim().min(1, 'Give the option a name.'),
  nameKm: z.string().trim().optional(),
  displayNameEn: z.string().trim().optional(),
  displayNameKm: z.string().trim().optional(),
  items: z
    .array(
      z.object({
        nameEn: z.string().trim().min(1, 'Name this value.'),
        nameKm: z.string().trim().optional(),
      }),
    )
    .min(1, 'An option needs at least one value.'),
})

type FormValues = z.infer<typeof schema>

interface OptionSetFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  optionSet: OptionSet | null
  onSaved: () => void
}

/**
 * An option axis and its values — `Size: S, M, L`.
 *
 * Unlike a choice group, the update endpoint takes the items with the body and
 * replaces them, so the whole set saves in one call and the form can hold the
 * values as plain rows.
 */
export function OptionSetFormDialog({
  isOpen,
  onOpenChange,
  optionSet,
  onSaved,
}: OptionSetFormDialogProps) {
  const { save } = useOptionSetActions()

  const { control, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      nameEn: optionSet?.name?.en ?? '',
      nameKm: optionSet?.name?.km ?? '',
      displayNameEn: optionSet?.displayName?.en ?? '',
      displayNameKm: optionSet?.displayName?.km ?? '',
      items: optionSet?.items?.length
        ? optionSet.items.map((item) => ({
            nameEn: item.name?.en ?? '',
            nameKm: item.name?.km ?? '',
          }))
        : [{ nameEn: '', nameKm: '' }],
    },
  })

  const items = useFieldArray({ control, name: 'items' })

  const submit = handleSubmit(async (values) => {
    const body: SaveOptionSetBody = {
      name: toTranslation(values.nameEn, values.nameKm),
      // The server reads `displayName` without a guard, so it always travels —
      // falling back to the name rather than to nothing.
      displayName: toTranslation(values.displayNameEn || values.nameEn, values.displayNameKm),
      items: values.items.map((item) => ({ name: toTranslation(item.nameEn, item.nameKm) })),
    }

    await save.mutateAsync({ id: optionSet?.id ?? null, body })
    onSaved()
  })

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={optionSet ? 'Edit option' : 'New option'}
      description="An axis your product variants are built from — size, colour, flavour."
      submitLabel={optionSet ? 'Save changes' : 'Create option'}
      onSubmit={submit}
      isBusy={save.isPending}
      error={save.error}
      errorFallback="Could not save this option."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextFieldControl
          control={control}
          name="nameEn"
          label="Name (English)"
          isRequired
          autoFocus
        />
        <TextFieldControl control={control} name="nameKm" label="Name (Khmer)" />
        <TextFieldControl
          control={control}
          name="displayNameEn"
          label="Shown as (English)"
          description="Optional. Defaults to the name."
        />
        <TextFieldControl control={control} name="displayNameKm" label="Shown as (Khmer)" />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Values</h3>
          <Button
            variant="ghost"
            size="sm"
            className="border-border h-8 rounded-full border px-3 text-xs"
            onPress={() => items.append({ nameEn: '', nameKm: '' })}
          >
            <PlusIcon width={14} height={14} />
            Add value
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          {items.fields.map((field, index) => (
            <div
              key={field.id}
              className="border-border grid gap-3 rounded-xl border p-3 sm:grid-cols-[1fr_1fr_auto]"
            >
              <TextFieldControl
                control={control}
                name={`items.${index}.nameEn`}
                label="Value (English)"
                isRequired
              />
              <TextFieldControl
                control={control}
                name={`items.${index}.nameKm`}
                label="Value (Khmer)"
              />
              <Button
                variant="ghost"
                aria-label="Remove value"
                isDisabled={items.fields.length === 1}
                onPress={() => items.remove(index)}
                className="text-muted hover:text-danger size-10 self-end rounded-full"
              >
                <TrashIcon width={16} height={16} />
              </Button>
            </div>
          ))}
        </div>

        {formState.errors.items?.message && (
          <p role="alert" className="text-danger text-xs">
            {formState.errors.items.message}
          </p>
        )}
      </div>
    </FormModal>
  )
}
